import { SyntheticEvent, useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useCompanyQuery, useProductQuery } from "../../../../../api/services";
import { FormAutocomplete } from "../../../../../components/fields";
import { ILocation, RentalLocationType, RentalType } from "../../../../../types";
import { isBusinessContinuityMode, isLeisureCode, stepState } from "../../../recoil";
import { IFormValues, InsertBookingStep } from "../../../types";
import LocationHelperText from "../location-helper-text/LocationHelperText";
import { convertUtcToTzIsoDayJs } from "../../../../../../../util/timezoneConversion";
import dayjs from "dayjs";

const FormPickupLocation = () => {
    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);
    const flagLeisureCode = useRecoilValue(isLeisureCode);

    const formContext = useFormContext<IFormValues>();

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });
    const customerCode = useWatch<IFormValues, 'basicDetails.customerCode'>({
        name: 'basicDetails.customerCode'
    });
    const pickupLocationWatch = useWatch<IFormValues, 'basicDetails.pickupLocation'>({
        name: 'basicDetails.pickupLocation'
    });
    var timezone: string | undefined = pickupLocationWatch?.zoneId ?? undefined;

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const productQuery = useProductQuery(
        {
            segment: rentalType,
            businessContinuityInsToolMode: businessContinuityMode,
            tenant: tenant as string,
            dealer: dealer as string
        },
        {
            refetchOnMount: false,
            enabled: !flagLeisureCode && rentalType === RentalType.RETAIL
        }
    );

    const companyQuery = useCompanyQuery(
        {
            code: customerCode,
            segment: rentalType,
            tenant: tenant as string,
            dealer: dealer as string,
        },
        {
            refetchOnMount: false,
            enabled: !flagLeisureCode && rentalType === RentalType.CORPORATE
        }
    )

    const locationsLoading = rentalType === RentalType.RETAIL
        ? productQuery.isLoading
        : companyQuery.isLoading;

    const locationOptions = rentalType === RentalType.RETAIL
        ? productQuery.data?.locations
        : companyQuery.data?.locations;

    function updateTimeZone(value: ILocation | null) {
        if (timezone != value?.zoneId) {
            timezone = value?.zoneId
            formContext.setValue(
                'basicDetails.pickupDate',
                convertUtcToTzIsoDayJs(dayjs().add(15, 'm').toISOString(), timezone)
            );
            formContext.setValue(
                'basicDetails.dropoffDate',
                convertUtcToTzIsoDayJs(dayjs().add(3, 'day').add(15, 'm').toISOString(), timezone)
            );
            formContext.setValue(
                'basicDetails.dropoffDate',
                convertUtcToTzIsoDayJs(dayjs().add(3, 'day').add(15, 'm').toISOString(), timezone)
            );
            formContext.setValue(
                'basicDetails.pickupLocationTimezone',
                value?.zoneId ?? "Europe/Rome"
            );
        }
    }

    const onChange = useCallback((_: SyntheticEvent<Element, Event>, value: ILocation | null) => {
        if (value) {
            formContext.trigger('basicDetails.pickupLocation');
        }
        formContext.trigger('basicDetails.pickupDate');

        updateTimeZone(value)
    }, [])

    return (
        <FormAutocomplete<IFormValues>
            name="basicDetails.pickupLocation"
            label={t('label.pickupLocation')}
            options={locationOptions ?? []}
            onChange={onChange}
            loading={locationsLoading}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
            textFieldProps={{
                helperText: <LocationHelperText location={pickupLocationWatch} locationType={RentalLocationType.Pickup} />
            }}
        />
    );
}

export default FormPickupLocation;
